import { request } from './client';
import type { PagedResponse } from './unitsOfMeasure';

/**
 * Loại thuế.
 *
 * Giá trị số phải khớp với phía máy chủ. Chín loại này không phải cách phân nhóm cho gọn: mỗi loại đi
 * vào một chỗ khác nhau trên tờ khai và hạch toán vào một tài khoản khác nhau.
 */
export const TaxType = {
  /** VAT đầu ra — bán hàng, cung cấp dịch vụ. */
  OutputValueAddedTax: 1,

  /** VAT đầu vào — mua hàng trong nước. */
  InputValueAddedTax: 2,

  /** VAT hàng nhập khẩu — dùng tỷ giá hải quan. */
  ImportValueAddedTax: 3,

  /** Thuế nhập khẩu — theo mã HS và xuất xứ. */
  ImportDuty: 4,

  /** Thuế nhà thầu nước ngoài — phần VAT. */
  ForeignContractorValueAddedTax: 5,

  /** Thuế nhà thầu nước ngoài — phần TNDN. */
  ForeignContractorCorporateIncomeTax: 6,

  /** Thuế thu nhập cá nhân. */
  PersonalIncomeTax: 7,

  /** Thuế thu nhập doanh nghiệp. */
  CorporateIncomeTax: 8,

  /** Thuế bảo vệ môi trường và loại khác. */
  Other: 9,
} as const;

/** Giá trị của loại thuế. */
export type TaxTypeValue = (typeof TaxType)[keyof typeof TaxType];

/** Tên tiếng Việt của từng loại thuế, dùng để hiển thị. */
export const taxTypeNames: Readonly<Record<TaxTypeValue, string>> = {
  [TaxType.OutputValueAddedTax]: 'VAT đầu ra',
  [TaxType.InputValueAddedTax]: 'VAT đầu vào',
  [TaxType.ImportValueAddedTax]: 'VAT hàng nhập khẩu',
  [TaxType.ImportDuty]: 'Thuế nhập khẩu',
  [TaxType.ForeignContractorValueAddedTax]: 'Thuế nhà thầu — VAT',
  [TaxType.ForeignContractorCorporateIncomeTax]: 'Thuế nhà thầu — TNDN',
  [TaxType.PersonalIncomeTax]: 'Thuế TNCN',
  [TaxType.CorporateIncomeTax]: 'Thuế TNDN',
  [TaxType.Other]: 'Thuế khác',
};

/** Một dòng thuế suất trong chuỗi hiệu lực. */
export interface TaxRate {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly taxType: TaxTypeValue;
  readonly ratePercent: number;
  readonly isValueAddedTaxExempt: boolean;
  readonly isNotDeclarable: boolean;
  readonly generalLedgerAccountCode: string | null;

  /** Ngày bắt đầu hiệu lực, dạng yyyy-MM-dd. */
  readonly validFrom: string;

  /** Ngày hết hiệu lực, dạng yyyy-MM-dd. Rỗng nghĩa là còn hiệu lực. */
  readonly validTo: string | null;

  readonly legalBasis: string | null;
  readonly isDefault: boolean;
  readonly isActive: boolean;

  /**
   * Đang có hiệu lực vào ngày nghiệp vụ hôm nay.
   *
   * Máy chủ tính sẵn vì nó phụ thuộc vào ngày nghiệp vụ theo múi giờ của công ty, mà máy của người dùng
   * có thể ở múi giờ khác hoặc đặt sai ngày.
   */
  readonly isEffectiveToday: boolean;

  /** Còn sửa được tỷ lệ tại chỗ, tức là dòng này chưa bắt đầu có hiệu lực. */
  readonly canChangeRateInPlace: boolean;
}

/** Điều kiện lọc danh sách thuế suất. */
export interface TaxRateFilter {
  readonly searchTerm?: string;
  readonly taxType?: TaxTypeValue;
  readonly effectiveOn?: string;
  readonly includeInactive?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Nội dung gửi lên khi khai thuế suất mới. */
export interface CreateTaxRatePayload {
  readonly code: string;
  readonly name: string;
  readonly taxType: TaxTypeValue;
  readonly ratePercent: number;
  readonly validFrom: string;
  readonly isValueAddedTaxExempt: boolean;
  readonly isNotDeclarable: boolean;
  readonly generalLedgerAccountCode: string | null;
  readonly legalBasis: string | null;
}

/** Nội dung gửi lên khi sửa phần mô tả. */
export interface UpdateTaxRateDescriptionPayload {
  readonly name: string;
  readonly generalLedgerAccountCode: string | null;
  readonly legalBasis: string | null;
}

/** Nội dung gửi lên khi sửa tỷ lệ tại chỗ. */
export interface ChangeTaxRateBehaviourPayload {
  readonly ratePercent: number;
  readonly isValueAddedTaxExempt: boolean;
  readonly isNotDeclarable: boolean;
}

/** Nội dung gửi lên khi đổi tỷ lệ từ một ngày. */
export interface SupersedeTaxRatePayload {
  readonly effectiveFrom: string;
  readonly ratePercent: number;
  readonly isValueAddedTaxExempt: boolean;
  readonly isNotDeclarable: boolean;
  readonly legalBasis: string | null;
}

const baseRoute = '/api/v1/platform/tax-rates';

/** Dựng phần truy vấn của địa chỉ, bỏ hẳn tham số rỗng. */
function buildQueryString(filter: TaxRateFilter): string {
  const parameters = new URLSearchParams();

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  if (filter.taxType !== undefined) {
    parameters.set('taxType', String(filter.taxType));
  }

  if (filter.effectiveOn !== undefined) {
    parameters.set('effectiveOn', filter.effectiveOn);
  }

  if (filter.includeInactive === true) {
    parameters.set('includeInactive', 'true');
  }

  if (filter.pageNumber !== undefined) {
    parameters.set('pageNumber', String(filter.pageNumber));
  }

  if (filter.pageSize !== undefined) {
    parameters.set('pageSize', String(filter.pageSize));
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang danh sách thuế suất, gồm cả chuỗi hiệu lực. */
export async function listTaxRates(
  filter: TaxRateFilter,
): Promise<PagedResponse<TaxRate>> {
  return await request<PagedResponse<TaxRate>>(`${baseRoute}${buildQueryString(filter)}`);
}

/** Khai một thuế suất mới. */
export async function createTaxRate(payload: CreateTaxRatePayload): Promise<TaxRate> {
  return await request<TaxRate>(baseRoute, { method: 'POST', body: payload });
}

/** Sửa tên, tài khoản hạch toán và căn cứ pháp lý. */
export async function updateTaxRateDescription(
  id: string,
  payload: UpdateTaxRateDescriptionPayload,
): Promise<TaxRate> {
  return await request<TaxRate>(`${baseRoute}/${id}/description`, {
    method: 'PUT',
    body: payload,
  });
}

/** Sửa tỷ lệ tại chỗ. Chỉ dùng được với dòng chưa bắt đầu có hiệu lực. */
export async function changeTaxRateBehaviour(
  id: string,
  payload: ChangeTaxRateBehaviourPayload,
): Promise<TaxRate> {
  return await request<TaxRate>(`${baseRoute}/${id}/rate`, { method: 'PUT', body: payload });
}

/**
 * Đổi tỷ lệ từ một ngày.
 *
 * Máy chủ đóng khoảng hiệu lực của dòng hiện tại và khai dòng mới trong một lần lưu. Đây là cách đúng
 * để đổi tỷ lệ của một mã đã dùng trên chứng từ.
 */
export async function supersedeTaxRate(
  id: string,
  payload: SupersedeTaxRatePayload,
): Promise<TaxRate> {
  return await request<TaxRate>(`${baseRoute}/${id}/supersede`, {
    method: 'POST',
    body: payload,
  });
}

/** Đặt một thuế suất làm mặc định của loại thuế đó. */
export async function setTaxRateDefault(id: string): Promise<TaxRate> {
  return await request<TaxRate>(`${baseRoute}/${id}/default`, { method: 'PUT', body: {} });
}

/** Ngừng dùng hoặc cho dùng lại một thuế suất. */
export async function setTaxRateActive(id: string, isActive: boolean): Promise<TaxRate> {
  return await request<TaxRate>(`${baseRoute}/${id}/active`, {
    method: 'PUT',
    body: { isActive },
  });
}
